// @ts-check
/* eslint-env browser */

/**
 * Handles select-all behaviour for extraction list row checkboxes.
 *
 * Usage:
 *   <extraction-list-select>
 *     <div class="extraction-list__select-all">...</div>
 *     <sortable-table>...</sortable-table>
 *   </extraction-list-select>
 */
class ExtractionListSelect extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #selectAllInput = null
  /** @type {HTMLElement | null} */
  #tabsContainer = null
  /** @type {EventListener | null} */
  #onTabClick = null

  connectedCallback () {
    this.#selectAllInput = this.querySelector('.extraction-list__select-all-input')
    if (!this.#selectAllInput) {
      return
    }

    this.#selectAllInput.addEventListener('change', () => {
      for (const input of this.#rowInputs()) {
        input.checked = this.#selectAllInput?.checked ?? false
      }
      this.#emitSelectionChange()
    })

    this.addEventListener('change', (event) => {
      const target = event.target
      if (!(target instanceof HTMLInputElement) || !target.classList.contains('extraction-list__select-input')) {
        return
      }

      this.#syncSelectAllState()
      this.#emitSelectionChange()
    })

    this.#tabsContainer = this.closest('.govuk-tabs')
    this.#onTabClick = (event) => {
      const target = event.target
      if (!(target instanceof Element) || !target.closest('.govuk-tabs__tab')) {
        return
      }
      this.resetSelection()
    }
    this.#tabsContainer?.addEventListener('click', this.#onTabClick)

    this.#syncSelectAllState()
    this.#emitSelectionChange()
  }

  disconnectedCallback () {
    if (this.#tabsContainer && this.#onTabClick) {
      this.#tabsContainer.removeEventListener('click', this.#onTabClick)
    }
  }

  /**
   * Clear checked rows and notify bulk action controls.
   */
  resetSelection () {
    for (const input of this.#rowInputs()) {
      input.checked = false
    }
    this.#syncSelectAllState()
    this.#emitSelectionChange()
  }

  /**
   * Move focus to a sensible place after rows are removed.
   */
  focusAfterRowRemoval () {
    /** @type {HTMLElement | null} */ (this.closest('.govuk-tabs')?.querySelector('.govuk-tabs__tab'))?.focus()
  }

  /**
   * Remove a row from the extraction table and update selection state.
   * @param {string} id
   */
  removeRowById (id) {
    const row = this.querySelector(`tbody .govuk-table__row[data-job-id="${CSS.escape(id)}"]`)
    if (row) {
      row.remove()
    }

    this.#ensureEmptyStateRow()
    this.#syncSelectAllState()
    this.#emitSelectionChange()
  }

  /** @returns {HTMLInputElement[]} */
  #rowInputs () {
    return /** @type {HTMLInputElement[]} */([...this.querySelectorAll('.extraction-list__select-input')])
  }

  #syncSelectAllState () {
    if (!this.#selectAllInput) {
      return
    }
    const inputs = this.#rowInputs()
    const checkedCount = inputs.filter((input) => input.checked).length
    this.#selectAllInput.checked = checkedCount > 0 && checkedCount === inputs.length
    this.#selectAllInput.indeterminate = checkedCount > 0 && checkedCount < inputs.length
  }

  #ensureEmptyStateRow () {
    const tbody = this.querySelector('tbody.govuk-table__body')
    if (!tbody) {
      return
    }

    if (tbody.querySelector('tr[data-job-id]')) {
      return
    }

    const existingEmptyState = tbody.querySelector('.extraction-list__empty-row')
    if (existingEmptyState) {
      return
    }

    const row = document.createElement('tr')
    row.className = 'govuk-table__row extraction-list__empty-row'
    row.innerHTML = '<td class="govuk-table__cell" colspan="7">No extractions found</td>'
    tbody.appendChild(row)
  }

  #emitSelectionChange () {
    const selected = /** @type {HTMLInputElement[]} */([...this.querySelectorAll('.extraction-list__select-input:checked')])

    this.dispatchEvent(new CustomEvent('extraction-selection-change', {
      detail: {
        count: selected.length,
        selectedIds: selected.map((input) => input.value)
      },
      bubbles: true
    }))
  }
}

customElements.define('extraction-list-select', ExtractionListSelect)
